import { MemberType } from "../../../../Enums/PersonEnums";
import type { IPersonCreate } from "../../../../interfaces/models/IPersonCreate";
import {
  PERSON_SOCIAL_FIELD_DEFS,
  canonicalSocialDisplayName,
  canonicalSocialProfileUrl,
  socialNetworkErrorKey,
} from "./socialNetworks";
import type { PersonFormErrors, PersonFormFieldKey } from "./personFormTypes";
import {
  canActorAssignPersonRole,
  getPersonRoleAssignmentErrorMessage,
  isDataAdminActor,
} from "./personDataAdminUtils";
import { isWorldServicesGroupFirmId } from "../../../../utils/wsgStaffFirms";

export type PersonFormValidationOutcome = {
  errors: PersonFormErrors;
  hasMissingFields: boolean;
  hasValidationFormatErrors: boolean;
};

export type PersonFormValidationOptions = {
  acceptedEmailDomains?: string[];
  /** When true, main office is required for members regardless of isAttorney. */
  requireMainOffice?: boolean;
  /** When true, title and bio are required for members regardless of the actor. */
  requireProfessionalProfileFields?: boolean;
  actorRole?: string;
  existingRole?: string;
};

function normalizeAcceptedDomain(d: string): string {
  return d.trim().toLowerCase().replace(/^@+/, "");
}

function hasConfiguredAcceptedEmailDomains(
  accepted: string[] | undefined,
): boolean {
  const list = accepted?.filter((d) => normalizeAcceptedDomain(d)) ?? [];
  return list.length > 0;
}

/**
 * Checks if the domain part of `firmEmail` matches any of the accepted domains.
 * If `acceptedDomains` is empty or not passed, no domain restriction is applied (returns true).
 */
export function isFirmEmailAllowedForDomains(
  firmEmail: string,
  acceptedDomains: string[] | undefined,
): boolean {
  const email = firmEmail.trim();
  if (!email) {
    return true;
  }
  const list = acceptedDomains?.filter((d) => normalizeAcceptedDomain(d)) ?? [];
  if (list.length === 0) {
    return true;
  }
  const at = email.lastIndexOf("@");
  if (at < 0) {
    return false;
  }
  const host = email.slice(at + 1).toLowerCase();
  for (const raw of list) {
    const dom = normalizeAcceptedDomain(raw);
    if (!dom) {
      continue;
    }
    if (host === dom) {
      return true;
    }
    if (host.endsWith(`.${dom}`)) {
      return true;
    }
  }
  return false;
}

const REQUIRED: PersonFormFieldKey[] = [
  "memberType",
  "name",
  "lastName",
  "firm",
  "firmEmail",
  "role",
  "username",
];

function isEmptyString(v: unknown): boolean {
  return (
    v === undefined || v === null || (typeof v === "string" && v.trim() === "")
  );
}

function isMissingRequiredStringField(v: unknown): boolean {
  if (v === undefined || v === null) {
    return true;
  }
  if (typeof v === "object") {
    return true;
  }
  if (typeof v === "boolean") {
    return true;
  }
  const s = String(v).trim();
  if (s === "") {
    return true;
  }
  const lower = s.toLowerCase();
  if (lower === "undefined" || lower === "null") {
    return true;
  }
  return false;
}

/** Acepta http(s) explícito o dominio sin esquema (p. ej. www.linkedin.com/... o linkedin.com/...). */
function isValidOptionalUrl(value: string | undefined): boolean {
  if (!value || !value.trim()) {
    return true;
  }
  const s = value.trim();
  const candidate = /^https?:\/\//i.test(s)
    ? s
    : `https://${s.replace(/^\/+/, "")}`;
  try {
    const u = new URL(candidate);
    return (
      (u.protocol === "http:" || u.protocol === "https:") && Boolean(u.hostname)
    );
  } catch {
    return false;
  }
}

function isValidOptionalEmail(value: string | undefined): boolean {
  if (!value || !value.trim()) {
    return true;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Validates full form. Member-specific rules can be extended later.
 */
export function validatePersonCreateForm(
  form: IPersonCreate,
  options?: PersonFormValidationOptions,
): PersonFormValidationOutcome {
  const errors: PersonFormErrors = {};
  let hasMissingFields = false;
  let hasValidationFormatErrors = false;

  for (const key of REQUIRED) {
    const val = form[key];
    if (key === "memberType") {
      if (
        val !== MemberType.member &&
        val !== MemberType.nonMember &&
        val !== MemberType.staff
      ) {
        errors.memberType = "This field is required";
        hasMissingFields = true;
      }
    } else if (isEmptyString(val)) {
      errors[key] = "This field is required";
      hasMissingFields = true;
    }
  }

  const requireProfessionalFields =
    options?.requireProfessionalProfileFields === true &&
    form.memberType === MemberType.member;

  if (
    (isDataAdminActor(options?.actorRole) || requireProfessionalFields) &&
    isEmptyString(form.title)
  ) {
    errors.title = "This field is required";
    hasMissingFields = true;
  }

  if (requireProfessionalFields && isEmptyString(form.bio)) {
    errors.bio = "This field is required";
    hasMissingFields = true;
  }

  if (form.unlistedTitleToggle) {
    if (isMissingRequiredStringField(form.unlistedTitle)) {
      errors.unlistedTitle = "This field is required";
      hasMissingFields = true;
    }
  }

  if (form.hasAssistant) {
    if (isEmptyString(form.assistantName)) {
      errors.assistantName = "This field is required";
      hasMissingFields = true;
    }
    if (isEmptyString(form.assistantEmail)) {
      errors.assistantEmail = "This field is required";
      hasMissingFields = true;
    } else if (!isValidOptionalEmail(form.assistantEmail)) {
      errors.assistantEmail = "Enter a valid email";
      hasValidationFormatErrors = true;
    }
  }

  if (hasConfiguredAcceptedEmailDomains(options?.acceptedEmailDomains)) {
    if (!isValidOptionalEmail(form.firmEmail)) {
      errors.firmEmail = "Enter a valid email";
      hasValidationFormatErrors = true;
    } else if (
      form.firmEmail?.trim() &&
      !isFirmEmailAllowedForDomains(
        form.firmEmail,
        options?.acceptedEmailDomains,
      )
    ) {
      errors.firmEmail = "Email must use an accepted domain for this firm";
      hasValidationFormatErrors = true;
    }
  } else if (isEmptyString(form.firmEmail)) {
    errors.firmEmail = "This field is required";
    hasMissingFields = true;
  }

  const social = form.socialNetworks ?? [];
  for (const entry of social) {
    if (!entry?.url?.trim()) {
      continue;
    }

    const canonical = canonicalSocialDisplayName(entry.name);
    const rawName = String(entry.name);
    const def = canonical
      ? PERSON_SOCIAL_FIELD_DEFS.find((d) => d.name === canonical)
      : PERSON_SOCIAL_FIELD_DEFS.find((d) => d.name === rawName);

    if (def) {
      if (canonicalSocialProfileUrl(entry.url, def.name) == null) {
        errors[socialNetworkErrorKey(def.name)] = "Enter a valid URL";
        hasValidationFormatErrors = true;
      }
      continue;
    }

    if (!isValidOptionalUrl(entry.url)) {
      errors.socialNetworks = "Enter a valid URL for each social profile";
      hasValidationFormatErrors = true;
    }
  }

  if (
    form.memberType === MemberType.member &&
    (options?.requireMainOffice || form.isAttorney) &&
    isEmptyString(form.mainOffice)
  ) {
    errors.mainOffice = options?.requireMainOffice
      ? "Main office is required"
      : "Main office is required for practicing attorneys";
    hasMissingFields = true;
  }

  const staffProfilesScreen = isWorldServicesGroupFirmId(form.firm);
  if (
    !canActorAssignPersonRole(
      options?.actorRole,
      String(form.role ?? "").trim(),
      options?.existingRole,
      { staffProfilesScreen },
    )
  ) {
    errors.role = getPersonRoleAssignmentErrorMessage(options?.actorRole, {
      staffProfilesScreen,
    });
    hasValidationFormatErrors = true;
  }

  if (form.isAttorney) {
    const aops = form.areasOfPractice;
    if (!Array.isArray(aops) || aops.length === 0) {
      errors.areasOfPractice = "Select at least one area of practice";
      hasMissingFields = true;
    }
    const myAop = form.aopExpertiseIds;
    if (!Array.isArray(myAop) || myAop.length === 0) {
      errors.aopExpertiseIds = "Select at least one in My Areas of Practice";
      hasMissingFields = true;
    }
  }

  return {
    errors,
    hasMissingFields,
    hasValidationFormatErrors,
  };
}

export function hasValidationErrors(errors: PersonFormErrors): boolean {
  return Object.values(errors).some((m) => Boolean(m));
}
